/**
 * TEXT A PDF CAN ACTUALLY DRAW.
 *
 * WHAT BROKE. On 24 August 2026 a change request refused to generate on live:
 * `WinAnsi cannot encode "−" (0x2212)`. That is the true MINUS SIGN, which is
 * not the hyphen on a keyboard — Word produces it, spreadsheets produce it, and
 * anybody pasting from either brings it in without seeing anything different.
 *
 * WHY IT IS FATAL RATHER THAN UGLY. The standard PDF faces are encoded in
 * WinAnsi, which holds about 220 characters. pdf-lib THROWS on anything outside
 * it — and it throws in `widthOfTextAtSize` as well as in `drawText`, so merely
 * measuring a line to wrap it is enough. One character somebody pasted into one
 * card takes the whole document down, and the message names a code point rather
 * than the card, the field, or the word.
 *
 * WHAT THIS DOES. Everything the reader would recognise is turned into its
 * plain equivalent — a minus becomes a hyphen, a smart quote becomes a quote,
 * an arrow becomes ->. What is left over becomes '?' rather than vanishing: a
 * character silently dropped from a document somebody signs is worse than a
 * visible mark that something was there.
 *
 * IT DOES NOT TOUCH WHAT IS ON SCREEN. The card keeps the character the person
 * typed. This is the last step before ink, and nowhere else.
 *
 * THE REAL FIX IS A FONT. Embedding a Unicode face would carry every one of
 * these properly and is a bigger change — a file to ship, a licence to check,
 * and every measurement in the document to re-check. Until then this stops the
 * crash and keeps the meaning.
 */

/**
 * The 27 characters WinAnsi carries above Latin-1 — the ones that would be
 * refused by a naive "code point below 256" test and are in fact perfectly
 * drawable. Written out because guessing at this range is how the smart quotes
 * in every pasted paragraph would turn into question marks.
 */
const ALSO_FINE = new Set(
  '€‚ƒ„…†‡ˆ‰Š‹ŒŽ' +
    '‘’“”•–—˜™š›œžŸ'
);

/** What a reader loses least by. Order matters only in that longer output is fine. */
const PLAIN: Record<string, string> = {
  '−': '-', // minus sign — the one that broke it
  '‐': '-', '‑': '-', '‒': '-', '―': '-', // hyphens and bars
  ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', '　': ' ', // spaces
  '​': '', '‌': '', '‍': '', '﻿': '', // zero-width: no mark at all
  '≤': '<=', '≥': '>=', '≠': '!=',
  '→': '->', '←': '<-', '⇒': '=>', '↔': '<->',
  '✓': 'Yes', '✔': 'Yes', '✗': 'No', '✘': 'No',
  /*
   * THESE TWO ARE DRAWABLE AND ARE REPLACED ANYWAY, which has a price worth
   * knowing: a document that generated fine before now generates DIFFERENT
   * BYTES. The file is deliberately a pure function of its content and is
   * fingerprinted on upload, so a change request regenerated after this
   * change will not match the fingerprint an earlier run produced. Nothing
   * stored breaks — the signed artefact is the uploaded file, not this
   * rendering — but that is the fact somebody needs when a fingerprint stops
   * matching and nobody can think why. Accepted for these two because both
   * wrap better as their plain forms; not a licence to add a third.
   */
  '…': '...',
  '●': '-', '▪': '-', '◦': '-', // bullets that are not U+2022
  '′': "'", '″': '"',
  /*
   * NOT '·', '´', '`', '×' OR '÷'. All five are drawable and mean themselves.
   * The backtick is ordinary ASCII and an over-eager table turned it into an
   * apostrophe. The multiplication and division signs were worse: both are
   * Latin-1, both render perfectly, and "3 × 4" quietly became "3 x 4" — a
   * change of meaning inside a document somebody signs, which is the one kind
   * of damage that reaches a customer with nobody noticing. Caught in review
   * on 24 August 2026. Do not put any of these five back.
   */
};

/** Space to tilde, then the Latin-1 upper half. Everything else is decided above. */
function drawable(c: string): boolean {
  const n = c.codePointAt(0) ?? 0;
  return (n >= 0x20 && n <= 0x7e) || (n >= 0xa0 && n <= 0xff) || ALSO_FINE.has(c);
}

/**
 * Make a string safe to DRAW. Call it before measuring too — pdf-lib refuses an
 * unencodable character in `widthOfTextAtSize` just as it does in `drawText`,
 * so cleaning up only at the point of drawing still crashes while wrapping.
 *
 * WITH ONE EXCEPTION, AND IT IS THE ONE THAT WILL CATCH SOMEBODY OUT: a
 * newline survives, on purpose, because `wrap()` splits paragraphs on it — and
 * `widthOfTextAtSize` refuses `0x0A` like anything else it cannot encode. So
 * the return value is safe to draw as it stands and must be SPLIT ON '\n'
 * BEFORE IT IS MEASURED. Every measurement in this codebase already happens
 * after that split, which is why nothing crashes today; the next one written
 * on multi-line text would reintroduce exactly the fault this file exists to
 * close. `test-a-pdf-can-draw-what-people-type` records both directions.
 */
export function winAnsi(text: string): string {
  let out = '';
  /*
   * NFC first, so a letter typed as "e" plus a combining accent becomes the
   * single é that WinAnsi holds. Without it the accent arrives as its own
   * character, is not drawable, and a French name loses its marks to question
   * marks for a reason nobody could see.
   */
  for (const c of String(text ?? '').normalize('NFC')) {
    /*
     * NEWLINES SURVIVE. They are below the drawable range and would otherwise
     * become '?' — and `wrap()` splits paragraphs on them, so a document would
     * lose every paragraph break and gain a question mark where each one was.
     * Caught while writing this, before it reached anything.
     */
    if (c === '\n') out += c;
    else if (c === '\r') continue; // a Windows line ending arrives as \r\n
    /*
     * THE TABLE IS CONSULTED FIRST, and that order is deliberate. Some of what
     * it replaces IS drawable — a non-breaking space, an ellipsis — and is
     * replaced anyway because the plain form wraps better and reads the same.
     * Asking `drawable` first meant those entries were never reached.
     */
    else if (c in PLAIN) out += PLAIN[c];
    else if (drawable(c)) out += c;
    else if (c === '\t') out += '    ';
    else out += '?';
  }
  return out;
}

/**
 * TEXT THAT WAS ALREADY WRAPPED BY SOMETHING ELSE.
 *
 * WHAT IT LOOKED LIKE. The Impact of Change section of a real change request
 * came out as a long line, then two or three words alone, over and over —
 * "…and the in-app", "…across all", "…allow". Reported by Vinnie on 24 August
 * 2026 with a screenshot, and visible in the stored text the moment it was
 * measured: six lines of 117, 118, 115, 113, 118 and 72 characters, no blank
 * lines anywhere. One paragraph, hard-wrapped at about 120 columns by whatever
 * produced it, then pasted in.
 *
 * WHY THAT BREAKS. `wrap()` treats every newline as the end of a paragraph, so
 * each 117-character line was wrapped on its own into a column narrower than
 * it — one full line, then the remainder stranded. The document was not
 * mis-measured; it was told six times that a paragraph had ended.
 *
 * WHAT A BLANK LINE MEANS, AND WHAT A SINGLE ONE DOES NOT. A blank line always
 * starts a new paragraph and is never touched. A single newline inside a block
 * is the ambiguous one: it is a wrap artefact in pasted prose and a deliberate
 * break in a list.
 *
 * SO IT IS DETECTED RATHER THAN ASSUMED. Hard wrapping leaves a fingerprint —
 * every line but the last comes out near the same width, because that is what
 * wrapping does. Deliberate breaks do not: a list, an address or a set of
 * headings have lines of obviously different lengths. A block is only rejoined
 * when its lines really do cluster near one width, and left exactly as typed
 * otherwise. Getting this wrong in the cautious direction leaves ragged text;
 * getting it wrong the other way would run somebody's list into a paragraph.
 */

/** A line somebody meant to stand alone, whatever its length. */
const A_LIST_LINE = /^\s*(?:[-*•\u2013\u2014]|\d+[.)]|[a-z][.)])\s+/i;

/** Below this, a line is short enough to have been a deliberate break. */
const WRAPPED_AT_LEAST = 60;

/** How alike the lines must be before it is called wrapping and not writing. */
const ALIKE = 0.75;

/**
 * PUT WRAPPED LINES BACK TOGETHER, minding the hyphen.
 *
 * A wrapper is allowed to break a line straight after a hyphen, which is how
 * "cancellation-related" arrives as "cancellation-" and "related" on the line
 * below. Joining those with a space says "cancellation- related", which is not
 * a word anybody wrote. Joined tight it is the compound again, and the same
 * rule repairs a wrapper that split a word in half.
 *
 * ONLY WHEN WHAT FOLLOWS IS LOWER CASE, because "Section A -" and "Dashboard"
 * is a dash between two things and wants its space kept.
 */
function rejoin(lines: string[]): string {
  return lines.reduce((said, line) =>
    said && /-$/.test(said) && /^[a-z0-9]/.test(line) ? said + line : said ? `${said} ${line}` : line
  , '');
}

export function unwrap(text: string): string {
  const out: string[] = [];
  /* Blank lines are the paragraph boundaries and survive untouched, so each
     block below is examined entirely on its own. */
  for (const block of String(text ?? '').split(/\n\s*\n/)) {
    const lines = block.split('\n');
    const widest = Math.max(0, ...lines.map((l) => l.length));
    const full = (l: string) => l.length >= widest * ALIKE;
    /*
     * A SHORT LINE ENDS A RUN AND NEVER SITS INSIDE ONE. Wrapping fills every
     * line but the last, so a short line in the middle of a block was put there
     * by a person. The block is therefore cut into runs at each short line —
     * that line belonging to the run it closes — and each run is judged alone.
     *
     * THE BLOCK USED TO BE JUDGED WHOLE, and one short line anywhere sank all
     * of it. Vinnie's change request on 24 August 2026 opened with a heading he
     * had pasted in with the text, "Change Description": eighteen characters
     * against a hundred and twelve. The four wrapped lines beneath it were
     * uniform and the heading was not, so the whole block was called deliberate,
     * every line became its own paragraph, and the section printed as a full
     * line followed by a single orphaned word, three times over. The heading is
     * now its own run and the four lines under it are rejoined, which is what
     * both of them plainly are.
     */
    const runs: string[][] = [];
    let run: string[] = [];
    for (const line of lines) {
      run.push(line);
      if (!full(line)) {
        runs.push(run);
        run = [];
      }
    }
    if (run.length) runs.push(run);

    /* Judged across the whole block, because a list stays a list whichever run
       a given item fell into. */
    const listed = lines.some((l) => A_LIST_LINE.test(l));

    const said = runs.map((run) => {
      /*
       * TWO FULL LINES AT LEAST, because one proves nothing. A run of two lines
       * — one long, one short — is exactly what a wrapped short paragraph looks
       * like AND exactly what two deliberately separate statements look like,
       * and a single sample always appears uniform to a test for uniformity.
       * With no evidence either way the text is left as written: a ragged pair
       * is ugly, and running two intended lines together in a document somebody
       * signs changes what it says. Caught by a test expecting exactly that.
       *
       * The last line of a run is short by nature, so dropping it leaves the
       * lines that had to have been filled by a wrapper.
       */
      const body = run.slice(0, -1);
      const wrapped = body.length >= 2 && widest >= WRAPPED_AT_LEAST && !listed;
      return wrapped ? rejoin(run.map((l) => l.trim())) : run.join('\n');
    });

    out.push(said.join('\n'));
  }
  return out.join('\n\n');
}
